import { DEFAULT_INCREMENT_TABLE } from './increments.js';
import type { AuctionMode, AuctionPolicy, RingState } from './types.js';

/**
 * Default auction policy per mode (ADR-42). Tenants override per sale; the
 * reducer never consults anything but `state.policy`.
 *
 *  - proxy: LIVE allows a private max on the lot in the ring; TIMED and
 *    LIVE_WITH_PREBIDDING allow it on any lot.
 *  - prebid.carryIn PROXY_AT_OPEN: a pre-bid lot's maxes run at OPEN_LOT and
 *    establish the opening high bid (recommended default).
 *  - announcements.onMaterial INVALIDATE_MAX_BIDS: a cribber disclosure, a papers
 *    change - a materially different animal. Silently bidding someone's max on
 *    it is wrong; each max is invalidated by an event and the bidder re-places
 *    to reconfirm (recommended default; PRESERVE / REQUIRE_RECONFIRM available).
 *  - timed: 2-minute soft-close window, 2-minute extension from the bid's
 *    server time, unlimited extensions, automatic disposition at close.
 */
export const DEFAULT_TIMED_CLOSE_POLICY: AuctionPolicy['timed'] = {
  extensionWindowMs: 2 * 60_000,
  extensionDurationMs: 2 * 60_000,
  maxExtensions: null,
  autoDisposition: true,
  extendFrom: 'BID_TIME',
};

/** ADR-60: a pending online bid waits 20 s for the operator by default. Long
 *  enough for a busy ring, short enough that a forgotten one does not hold
 *  every other bidder at BUSY. */
export const DEFAULT_PENDING_TTL_MS = 20_000;

export function defaultAuctionPolicy(mode: AuctionMode = 'LIVE'): AuctionPolicy {
  return {
    mode,
    // ADR-70 cut 3: off, so a ring behaves exactly as before unless a sale
    // asks for lots to wait for the operator (Q5).
    live: { startLotsPaused: false },
    proxy: { enabled: true, scope: mode === 'LIVE' ? 'CURRENT_LOT' : 'ANY_LOT' },
    prebid: { carryIn: 'PROXY_AT_OPEN' },
    announcements: { onMaterial: 'INVALIDATE_MAX_BIDS', requireAckOnMaterial: true },
    timed: DEFAULT_TIMED_CLOSE_POLICY,
    confirm: { pendingTtlMs: DEFAULT_PENDING_TTL_MS },
    sessions: { policy: 'MULTIPLE_VIEW_ONLY_ONE_BIDDING' },
    // ADR-70 cut 2: the brief's default for a live traditional sale. The
    // first committed bid still takes the board, exactly as before; the
    // auctioneer may now override an equal bid, on the record.
    equalBid: { policy: 'AUCTIONEER_AUTHORITY' },
  };
}

/** Deep-ish merge of a partial policy over the mode's defaults. */
export function resolveAuctionPolicy(input?: Partial<AuctionPolicy> & { readonly mode?: AuctionMode }): AuctionPolicy {
  const base = defaultAuctionPolicy(input?.mode ?? 'LIVE');
  if (!input) return base;
  return {
    mode: base.mode,
    live: { ...base.live, ...(input.live ?? {}) },
    proxy: { ...base.proxy, ...(input.proxy ?? {}) },
    prebid: { ...base.prebid, ...(input.prebid ?? {}) },
    announcements: { ...base.announcements, ...(input.announcements ?? {}) },
    timed: { ...base.timed, ...(input.timed ?? {}) },
    confirm: { ...base.confirm, ...(input.confirm ?? {}) },
    sessions: { ...base.sessions, ...(input.sessions ?? {}) },
    equalBid: { ...base.equalBid, ...(input.equalBid ?? {}) },
  };
}

export interface CreateRingStateInput {
  readonly tenantId: string;
  readonly saleId: string;
  readonly ringId: string;
  readonly incrementTableId?: string;
  readonly confirmMode?: boolean;
  /** Mode + policy overrides (ADR-42). Omitted → LIVE with defaults. */
  readonly policy?: Partial<AuctionPolicy> & { readonly mode?: AuctionMode };
  /** Provisioning generation this ring is created for (ADR-48). */
  readonly provisioningId?: string;
}

/** A fresh ring, before any lot is brought up.
 *
 *  `floorInputHealthyAt` starts null, and the auto-pause rule (spec 9.1) only
 *  fires during an open lot - so a ring that has not started yet is not
 *  considered to have lost floor input. */
export function createRingState(input: CreateRingStateInput): RingState {
  return {
    tenantId: input.tenantId,
    saleId: input.saleId,
    ringId: input.ringId,
    engineEpoch: 1,
    lastSeq: 0,
    nextSeq: 1,
    finalizedSeq: null,
    policy: resolveAuctionPolicy(input.policy),
    provisioningId: input.provisioningId ?? null,
    lots: {},
    maxBids: {},
    currentLotId: null,
    lotState: 'UPCOMING',
    closeStep: null,
    currentHighBid: null,
    currentHighBidder: null,
    currentHighBidSource: null,
    currentHighPaddle: null,
    currentHighBidSeq: null,
    currentAsk: null,
    onlineStatus: 'OPEN',
    pauseReason: null,
    confirmMode: input.confirmMode ?? false,
    floorInputHealthyAt: null,
    lastBid: null,
    incrementTableId: input.incrementTableId ?? DEFAULT_INCREMENT_TABLE.id,
    bidders: {},
    pendingBid: null,
    sellingGroup: null,
    equalBidAward: null,
    previousLot: null,
    lastDeparted: null,
    chosenIncrement: null,
    sales: {},
    requiredAcks: {},
    acknowledgements: {},
    ringStaff: null,
    capabilities: null,
  };
}
